import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Layers, ArrowRight } from "lucide-react";
import { OEM_PRODUCTS } from "../data/content";
import { Button } from "./ui/button";
import { useModals } from "./modals/ModalProvider";

export default function OEMShowcase() {
  const [active, setActive] = useState(OEM_PRODUCTS[0].id);
  const current = OEM_PRODUCTS.find((o) => o.id === active) || OEM_PRODUCTS[0];
  const { openBookConsultation } = useModals();

  return (
    <section id="oem-products" className="relative py-20 sm:py-28 overflow-hidden">
      {/* colorful backdrop */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 left-1/4 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 grid-pattern opacity-20" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary flex items-center gap-2">
            <Layers className="h-4 w-4" /> OEM Products &amp; Partnerships
          </p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            World-class technology, <span className="text-gradient-brand">expertly delivered.</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Techmate architects, deploys and manages best-of-breed OEM security and cloud platforms —
            the same products trusted by Fortune 500 enterprises, sized and configured for your business.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          {/* Left rail: OEM selector */}
          <div className="lg:col-span-4" data-testid="oem-selector">
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
              {OEM_PRODUCTS.map((o) => {
                const isActive = active === o.id;
                return (
                  <button
                    key={o.id}
                    onClick={() => setActive(o.id)}
                    data-testid={`oem-tab-${o.id}`}
                    className={`text-left rounded-2xl border transition-all p-3.5 sm:p-4 group ${
                      isActive
                        ? "border-primary bg-primary/[0.06] shadow-lg shadow-primary/10"
                        : "border-border bg-card hover:border-primary/40 hover:bg-primary/[0.02]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-16 w-20 rounded-lg bg-white border border-border grid place-items-center overflow-hidden shrink-0">
                        {o.logo ? (
                          <img src={o.logo} alt={o.name} className="max-h-12 max-w-full object-contain" loading="lazy" />
                        ) : (
                          <span className="text-xs font-black tracking-tight text-slate-800">{o.name}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-display text-sm sm:text-base font-bold leading-tight truncate">
                          {o.name}
                        </div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5 truncate">
                          {o.category}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right panel: OEM details */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                data-testid={`oem-detail-${current.id}`}
                className="relative overflow-hidden rounded-3xl border border-border bg-card p-6 sm:p-8 lg:p-10"
              >
                <div
                  className="absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-15 blur-3xl"
                  style={{ background: current.color }}
                />
                <div className="relative">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    <div className="h-28 w-44 rounded-2xl bg-white border border-border grid place-items-center overflow-hidden shrink-0 shadow-sm">
                      {current.logo ? (
                        <img src={current.logo} alt={current.name} className="max-h-20 max-w-full object-contain" />
                      ) : (
                        <span className="font-display font-black text-2xl text-slate-800">{current.name}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: current.color }}>
                        {current.category}
                      </p>
                      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{current.name}</h3>
                      <p className="mt-1 text-sm sm:text-base text-muted-foreground italic">{current.tagline}</p>
                    </div>
                  </div>

                  <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    {current.features.map((f) => (
                      <div
                        key={f}
                        className="flex items-start gap-3 rounded-xl border border-border bg-background/70 p-3.5 hover:border-primary/40 transition-colors"
                      >
                        <div
                          className="mt-0.5 h-6 w-6 rounded-lg grid place-items-center shrink-0"
                          style={{ background: `${current.color}18`, color: current.color }}
                        >
                          <Check className="h-3.5 w-3.5" strokeWidth={3} />
                        </div>
                        <span className="text-sm leading-relaxed text-foreground/90">{f}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <Button
                      onClick={openBookConsultation}
                      data-testid={`oem-cta-${current.id}`}
                      className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      Discuss {current.name} for my business <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
